import { useContext, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import CompanyContext from '../../../contexts/company/CompanyContext';
import { getConversationThread } from '../../../../domain/use_cases/conversations/getConversationThread';
import { ConversationThreadItem } from '../../../../domain/models/Conversation';
import { getConversationKey, groupThreadByDay } from '../types/ConversationDisplay';

// The thread of the selected conversation, fetched again each time another one is opened
const useConversationThreadController = (subscriptionId: string, phone: string) => {
  const { authentication } = useContext(AuthenticationContext);
  const { activeCompany } = useContext(CompanyContext);

  const [loaded, setLoaded] = useState<{ key: string; items: ConversationThreadItem[] } | null>(null);
  const [failedKey, setFailedKey] = useState<string | null>(null);

  const companyId = activeCompany?.id;
  const key = getConversationKey(subscriptionId, phone);
  const isLoaded = loaded?.key === key;
  const hasFailed = failedKey === key;

  useEffect(() => {
    if (isLoaded || hasFailed || !companyId || !authentication) return;

    let isCancelled = false;
    getConversationThread({ company_id: companyId, subscription_id: subscriptionId, phone }, authentication)
      .then((response) => {
        if (!isCancelled) setLoaded({ key, items: response.items });
      })
      .catch((e) => {
        if (isCancelled) return;
        message.error(e instanceof Error ? e.message : 'Erreur lors du chargement de la conversation');
        setFailedKey(key);
      });
    return () => {
      isCancelled = true;
    };
  }, [isLoaded, hasFailed, key, subscriptionId, phone, companyId, authentication]);

  const days = useMemo(() => groupThreadByDay(isLoaded ? loaded.items : []), [isLoaded, loaded]);

  return {
    state: {
      days,
      isLoading: !isLoaded && !hasFailed,
      hasFailed,
    },
  };
};

export default useConversationThreadController;
